/**
 * Landmark Zone Detection Utility
 * Handles premium zone detection around famous landmarks
 */

import landmarksData from '../landmarks.json';
import { getLandmarkZonePricing } from '../config/featurePricing';

/**
 * Calculate distance between two points using Haversine formula
 * @param {number} lat1 - First point latitude
 * @param {number} lng1 - First point longitude
 * @param {number} lat2 - Second point latitude
 * @param {number} lng2 - Second point longitude
 * @returns {number} Distance in kilometers
 */
export function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth's radius in km
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Convert degrees to radians
 * @param {number} degrees
 * @returns {number}
 */
function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Check if a location is within any landmark's premium zone
 * @param {number} lat - Location latitude
 * @param {number} lng - Location longitude
 * @returns {{ landmark: Object, distance: number, radiusKm: number, gemCost: number } | null}
 */
export function checkLandmarkZone(lat, lng) {
  const landmarks = landmarksData.landmarks;

  for (const landmark of landmarks) {
    const rarity = landmark.rarity;
    const pricing = getLandmarkZonePricing(rarity);

    if (!pricing) continue;

    const distance = calculateDistance(lat, lng, landmark.lat, landmark.lng);

    if (distance <= pricing.radiusKm) {
      return {
        landmark: {
          id: landmark.id,
          name: landmark.name,
          emoji: landmark.emoji,
          city: landmark.city,
          country: landmark.country,
          rarity: landmark.rarity,
          lat: landmark.lat,
          lng: landmark.lng,
        },
        distance: Math.round(distance * 1000), // in meters
        radiusKm: pricing.radiusKm,
        gemCost: pricing.gemCost,
      };
    }
  }

  return null;
}

/**
 * Get all landmarks with their zone info for map rendering
 * @returns {Array<{ landmark: Object, radiusKm: number, gemCost: number }>}
 */
export function getAllLandmarkZones() {
  return landmarksData.landmarks.map(landmark => {
    const pricing = getLandmarkZonePricing(landmark.rarity);
    return {
      landmark: {
        id: landmark.id,
        name: landmark.name,
        emoji: landmark.emoji,
        lat: landmark.lat,
        lng: landmark.lng,
        rarity: landmark.rarity,
      },
      radiusKm: pricing?.radiusKm || 0.5,
      gemCost: pricing?.gemCost || 250,
    };
  });
}

/**
 * Get zone styling by rarity for map rendering
 * @param {string} rarity - 'legendary', 'epic', or 'rare'
 * @returns {{ fillColor: string, borderColor: string }}
 */
export function getZoneStyling(rarity) {
  const styles = {
    legendary: {
      fillColor: 'rgba(255, 215, 0, 0.15)',   // gold
      borderColor: 'rgba(255, 215, 0, 0.5)',
    },
    epic: {
      fillColor: 'rgba(147, 112, 219, 0.15)', // purple
      borderColor: 'rgba(147, 112, 219, 0.5)',
    },
    rare: {
      fillColor: 'rgba(65, 105, 225, 0.15)',  // blue
      borderColor: 'rgba(65, 105, 225, 0.5)',
    },
  };

  return styles[rarity] || styles.rare;
}

const landmarkZonesUtils = { checkLandmarkZone, calculateDistance, getAllLandmarkZones, getZoneStyling };
export default landmarkZonesUtils;
